import { RP_LIST_CAPS, type RpSectionOf } from '@mlm/shared';
import { cn } from '../../lib/cn';
import { storefrontIcon } from '../../lib/storefront-icons';
import { useRpCtx } from '../context';
import { RpSectionHeader } from '../primitives';
import { rpCols } from '../tokens';

/**
 * The list-shaped persuasion sections: steps, testimonials, FAQ, stats, logo
 * strip and gallery.
 *
 * Grouped in one file because they are the same component five times over — a
 * header, a capped list, a grid — differing only in what a row looks like.
 * Every one clamps its list on read: a page stored before a cap was tightened
 * must still render.
 */

export function RpSteps({ section }: { section: RpSectionOf<'steps'> }) {
  const c = section.config;
  const items = c.items.slice(0, RP_LIST_CAPS.stepsItems);
  if (!items.length && !c.heading) return null;
  return (
    <div className="space-y-8">
      <RpSectionHeader heading={c.heading} subheading={c.subheading} />
      {items.length ? (
        <ol
          className={cn(
            'grid gap-6',
            c.layout === 'stack'
              ? 'grid-cols-1'
              : rpCols(Math.min(items.length, 4) as 1 | 2 | 3 | 4),
          )}
        >
          {items.map((it, i) => (
            <li key={i} className="space-y-2">
              {/* The numeral comes from position, never from the author — so
                  reordering steps can't leave them numbered 1, 3, 2. */}
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary font-heading text-sm font-bold text-primary-foreground">
                {i + 1}
              </span>
              <h3 className="font-heading text-base font-semibold">{it.title}</h3>
              {it.body ? <p className="text-sm opacity-80">{it.body}</p> : null}
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}

export function RpTestimonials({ section }: { section: RpSectionOf<'testimonials'> }) {
  const ctx = useRpCtx();
  const c = section.config;
  const items = c.items.slice(0, RP_LIST_CAPS.testimonialItems);
  if (!items.length && !c.heading) return null;
  return (
    <div className="space-y-8">
      <RpSectionHeader heading={c.heading} subheading={c.subheading} />
      {items.length ? (
        <div className={cn('grid gap-6', rpCols(c.cols ?? 3))}>
          {items.map((t, i) => (
            <figure
              key={i}
              className="space-y-3 rounded-lg bg-card p-6 text-left text-card-foreground shadow-card"
            >
              {t.rating ? (
                <div className="text-sm text-primary" aria-label={`${t.rating} out of 5`}>
                  {'★'.repeat(t.rating)}
                  <span className="opacity-30">{'★'.repeat(5 - t.rating)}</span>
                </div>
              ) : null}
              <blockquote className="text-sm leading-relaxed opacity-90">“{t.quote}”</blockquote>
              <figcaption className="flex items-center gap-3">
                {t.avatarKey ? (
                  <img
                    src={ctx.imageUrl(t.avatarKey)}
                    alt=""
                    className="h-9 w-9 rounded-full object-cover"
                  />
                ) : null}
                <span>
                  <span className="block text-sm font-semibold">{t.name}</span>
                  {t.role ? <span className="block text-xs opacity-70">{t.role}</span> : null}
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function RpFaq({ section }: { section: RpSectionOf<'faq'> }) {
  const c = section.config;
  const items = c.items.slice(0, RP_LIST_CAPS.faqItems);
  if (!items.length && !c.heading) return null;
  return (
    <div className="space-y-6">
      <RpSectionHeader heading={c.heading} subheading={c.subheading} />
      {items.length ? (
        <div className="space-y-2 text-left">
          {items.map((qa, i) => (
            // Native <details>: no JavaScript, correct keyboard and screen-reader
            // behavior for free, and zero bytes shipped to a prospect who is
            // probably on a phone on mobile data. A JS accordion would buy
            // nothing here.
            <details key={i} className="group rounded-lg border px-4 py-3">
              <summary className="cursor-pointer list-none text-sm font-medium marker:content-none">
                <span className="flex items-center justify-between gap-3">
                  {qa.q}
                  <span
                    aria-hidden
                    className="shrink-0 opacity-50 transition-transform group-open:rotate-45"
                  >
                    +
                  </span>
                </span>
              </summary>
              <p className="mt-2 whitespace-pre-wrap text-sm opacity-80">{qa.a}</p>
            </details>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function RpStats({ section }: { section: RpSectionOf<'stats'> }) {
  const c = section.config;
  const items = c.items.slice(0, RP_LIST_CAPS.statsItems);
  if (!items.length) return null;
  return (
    <div className="space-y-6">
      <RpSectionHeader heading={c.heading} />
      <div className={cn('grid gap-6', rpCols(Math.min(items.length, 4) as 1 | 2 | 3 | 4))}>
        {items.map((s, i) => {
          const Icon = s.icon ? storefrontIcon(s.icon) : null;
          return (
            <div key={i} className="space-y-1">
              {Icon ? <Icon className="mx-auto h-6 w-6 opacity-70" aria-hidden /> : null}
              <p className="font-heading text-3xl font-bold">{s.value}</p>
              <p className="text-sm opacity-70">{s.label}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function RpLogoStrip({ section }: { section: RpSectionOf<'logoStrip'> }) {
  const ctx = useRpCtx();
  const c = section.config;
  const items = c.items.slice(0, RP_LIST_CAPS.logoStripItems).filter((i) => i.imageKey);
  if (!items.length) return null;
  return (
    <div className="space-y-4">
      <RpSectionHeader heading={c.heading} />
      <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
        {items.map((l, i) => (
          <img
            key={i}
            src={ctx.imageUrl(l.imageKey)}
            alt={l.alt ?? ''}
            className={cn(
              'h-8 w-auto object-contain',
              c.grayscale !== false && 'opacity-60 grayscale',
            )}
          />
        ))}
      </div>
    </div>
  );
}

export function RpGallery({ section }: { section: RpSectionOf<'gallery'> }) {
  const ctx = useRpCtx();
  const c = section.config;
  const items = c.items.slice(0, RP_LIST_CAPS.galleryItems).filter((i) => i.imageKey);
  if (!items.length) return null;
  return (
    <div className="space-y-6">
      <RpSectionHeader heading={c.heading} />
      <div className={cn('grid gap-3', rpCols(c.cols ?? 3))}>
        {items.map((g, i) => (
          <figure key={i} className="space-y-1">
            <img
              src={ctx.imageUrl(g.imageKey)}
              alt={g.caption ?? ''}
              className="w-full rounded-lg object-cover"
            />
            {g.caption ? <figcaption className="text-xs opacity-70">{g.caption}</figcaption> : null}
          </figure>
        ))}
      </div>
    </div>
  );
}
